import React, { useEffect, useState } from 'react';

import DOMPurify from 'dompurify';
import { Controller, useForm } from 'react-hook-form';
import clsx from 'clsx';

import Loader from 'components/Loader/index';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { Lender } from 'enums/ApplicationsVariables';
import styles from 'pages/Dashboard/steps/AcceptOffer/CreditServicesAgreementPopup/CreditServicesAgreementPopup.module.scss';
import BlurPopup from 'components/BlurPopup';
import FormBody from 'components/FormBody';
import { Button } from 'components/Buttons/Button';
import { ButtonType } from 'enums/ButtonType';
import { getMessageForRequiredFields } from 'utils/validationUtils';
import { Checkbox, CheckboxType } from 'components/Checkbox';
import { documentsApi } from 'api/DocumentsApi';

interface CreditServicesAgreementPopupProps {
  isModalOpen: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  lender: Lender;
}

const CreditServicesAgreementPopup = ({
  isModalOpen,
  onClose,
  onSubmit,
  lender,
}: CreditServicesAgreementPopupProps) => {
  const dispatch = useAppDispatch();
  const { applicationId } = useAppSelector((state) => state.application);
  const [creditServicesAgreementDocument, setCreditServicesAgreementDocument] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const {
    watch,
    handleSubmit,
    control,
    formState: { errors, isSubmitted },
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      agreementCheckbox: false,
    },
  });
  const watcher = watch();

  const handleAccept = handleSubmit(async () => {
    const validationResult = await trigger();

    if (!validationResult) {
      return;
    }

    await onSubmit();
  });

  useEffect(() => {
    const getCreditAgreementsHtml = async () => {
      const creditServicesAgreementResponse = await documentsApi.downloadCreditServicesAgreement(
        applicationId!,
        lender,
      );

      setCreditServicesAgreementDocument(creditServicesAgreementResponse);
      setIsLoading(false);
    };

    getCreditAgreementsHtml();
  }, [dispatch, applicationId, lender]);

  return (
    <>
      <BlurPopup
        id="popup"
        classes={{
          paper: styles.paper,
          container: styles.container,
        }}
        onClose={onClose}
        open={isModalOpen}
        scroll="body"
        blurDeep={2}
      >
        <FormBody formBodyClassName={styles.form}>
          <p className={styles.title}>Credit Service Agreement</p>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <div
                className={styles.agreementText}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(creditServicesAgreementDocument, { FORCE_BODY: true }),
                }}
              />
              <Controller
                name="agreementCheckbox"
                control={control}
                rules={{
                  required: getMessageForRequiredFields(),
                  validate: (value) => value === true || getMessageForRequiredFields(),
                }}
                render={() => (
                  <div className={clsx(styles.checkboxWrapper)}>
                    <Checkbox
                      type={CheckboxType.Alternative}
                      name="agreementCheckbox"
                      isChecked={!!watcher.agreementCheckbox}
                      onChange={() => {
                        setValue('agreementCheckbox', !watcher.agreementCheckbox);

                        if (isSubmitted) {
                          trigger();
                        }
                      }}
                      isValid={!errors?.agreementCheckbox?.message}
                      text={
                        <>
                          {`I/We agree to `}
                          <span>Credit Services Agreement</span>
                        </>
                      }
                      labelClassName={styles.checkboxTextContainer}
                    />
                    <p className={styles.errorMessage}>{errors?.agreementCheckbox?.message}</p>
                  </div>
                )}
              />
            </>
          )}
          <div className={styles.buttonsContainer}>
            <Button className={styles.backButton} buttonType={ButtonType.Secondary} withArrow onClick={onClose}>
              Back
            </Button>
            <Button isLoading={isLoading} className={styles.acceptOfferButton} onClick={handleAccept}>
              Accept
            </Button>
          </div>
        </FormBody>
      </BlurPopup>
    </>
  );
};

export default CreditServicesAgreementPopup;
